/* eslint-disable no-param-reassign */
/* eslint-disable complexity */

/**
 * The function `destroyZoom` removes the 'swiper-no-swiping' class from the `swiper` element and
 * destroys the `wzoom` object.
 * @param wzoom - The `wzoom` parameter is likely referring to an instance of a Zoom library or plugin
 * that is being used for zooming functionality on the webpage.
 * @param swiper - The `swiper` parameter is a reference to an HTML element that has the class
 * `swiper-no-swiping`.
 */
export const destroyZoom = async (wzoom, swiper) => {
  swiper.classList.remove('swiper-no-swiping');
  wzoom.destroy();
};

/* eslint-disable max-lines-per-function */
/**
 * The `initZoom` function initializes a zoom feature for an element, allowing users to zoom in and out
 * on an image and navigate using keyboard arrow keys.
 * @param element - The `element` parameter is the DOM element that contains the image to be zoomed.
 * @returns the `wzoom` object.
 */
export const initZoom = async (element) => {
  const zoomUpButton = element.querySelector(
    '.product-media__zoom__button--in'
  );
  const zoomDownButton = element.querySelector(
    '.product-media__zoom__button--out'
  );
  let canMoveKeyboard = false;

  function getState(wzoom) {
    return {
      top: wzoom.content.currentTop,
      left: wzoom.content.currentLeft,
      scale: wzoom.content.currentScale,
    };
  }

  function transformWithinLimits(value, limit) {
    if (value > limit) {
      return limit - 1;
    }
    if (value < -limit) {
      return -limit + 1;
    }
    return value;
  }

  const wzoom = WZoom.create(element.querySelector('img'), {
    disableWheelZoom: true,
    speed: 5,
    maxScale: 2,
    minScale: 1,
    zoomOnClick: false,
    rescale: function (instance) {
      const { minScale, currentScale, maxScale } = instance.content;

      if (currentScale === minScale) {
        element.classList.remove('swiper-no-swiping');
        zoomDownButton.setAttribute('disabled', 'true');
      } else {
        element.classList.add('swiper-no-swiping');
        zoomDownButton.setAttribute('disabled', 'false');
      }

      if (currentScale === maxScale) {
        zoomUpButton.setAttribute('disabled', 'true');
      } else {
        zoomUpButton.setAttribute('disabled', 'false');
      }

      if (instance.content.minScale !== instance.content.currentScale) {
        canMoveKeyboard = true;
        element.querySelector('img').setAttribute('draggable', false);
      } else {
        element.querySelector('img').setAttribute('draggable', true);
        canMoveKeyboard = false;
      }
    },
    onMove: function (event, instance) {
      const { minScale, currentScale } = instance.content;
      if (currentScale === minScale) {
        wzoonEvent.transform(0, 0, 1);
      }
    },
    onDrop: function (event, instance) {
      const { minScale, currentLeft, currentTop, currentScale } =
        instance.content;
      // Fix mobile drag bug
      wzoonEvent.transform(
        transformWithinLimits(currentTop, 100),
        transformWithinLimits(currentLeft, 100),
        currentScale === minScale ? 1 : currentScale
      );

      if (currentScale === minScale) {
        wzoonEvent.transform(0, 0, 1);
      }
    },
  });

  let wzoonEvent = wzoom;

  zoomUpButton.addEventListener('click', () => {
    const state = getState(wzoom);
    const zoom =
      state.scale >= wzoom.content.maxScale
        ? wzoom.content.maxScale
        : state.scale + 0.5;
    wzoom.transform(state.top, state.left, zoom);
  });

  zoomDownButton.addEventListener('click', () => {
    const state = getState(wzoom);
    const zoom =
      state.scale <= wzoom.content.minScale
        ? wzoom.content.minScale
        : state.scale - 0.5;

    wzoom.transform(0, 0, zoom);
  });

  window.addEventListener('resize', () => {
    if (wzoom) {
      wzoom.prepare();
      wzoom.maxZoomDown();
    }
  });

  window.addEventListener(
    'keydown',
    (e) => {
      if (
        ['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].indexOf(
          e.code
        ) > -1
      ) {
        e.preventDefault();
      }
    },
    false
  );

  document.addEventListener(
    'keyup',
    (e) => {
      if (!canMoveKeyboard) return;
      if (e.code === 'ArrowUp') {
        e.preventDefault();
        const state = getState(wzoom);
        const top = state.top + 30;
        wzoom.transform(top, state.left, state.scale);
      }
      if (e.code === 'ArrowDown') {
        e.preventDefault();
        const state = getState(wzoom);
        const top = state.top - 30;
        wzoom.transform(top, state.left, state.scale);
      }
      if (e.code === 'ArrowLeft') {
        e.preventDefault();
        const state = getState(wzoom);
        const left = state.left + 30;
        wzoom.transform(state.top, left, state.scale);
      }
      if (e.code === 'ArrowRight') {
        e.preventDefault();
        const state = getState(wzoom);
        const left = state.left - 30;
        wzoom.transform(state.top, left, state.scale);
      }
    },
    false
  );

  return wzoom;
};
